import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { useIsMobile } from '@/lib/hooks'
import { formatCST } from '@/lib/time'
import {
  REPLY_CONTEXTS,
  REPLY_CONTEXT_LABELS,
  facetLabel,
  formatReplyTemplateError,
  listReplyTemplates,
  replyTemplatesKey,
  setReplyTemplateActive,
  slotActionLabel,
  upsertReplyTemplate,
  type ReplyContext,
  type ReplySlot,
  type ReplyTemplate,
} from '@/lib/reply-templates'

export const Route = createFileRoute('/_authed/reply-templates')({
  component: ReplyTemplatesPage,
})

type ActiveFilter = 'active' | 'all'

interface TemplateRow extends ReplyTemplate {
  slot: ReplySlot
}

interface FormValues {
  slotKey: string
  facet: string
  title: string
  body: string
  sort_order: number
}

const GENERIC_FACET = '__generic__'
const slotKey = (s: Pick<ReplySlot, 'context' | 'action'>) =>
  `${s.context}:${s.action}`

function ReplyTemplatesPage() {
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const isMobile = useIsMobile()
  const [form] = Form.useForm<FormValues>()
  const [context, setContext] = useState<ReplyContext>('episode_feedback_issue')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('active')
  const [editing, setEditing] = useState<TemplateRow | 'new' | null>(null)

  const listQuery = useQuery({
    queryKey: replyTemplatesKey(true),
    queryFn: () => listReplyTemplates(true),
  })

  const slots = useMemo(
    () => (listQuery.data ?? []).filter((s) => s.context === context),
    [listQuery.data, context],
  )
  const rows: TemplateRow[] = useMemo(
    () =>
      slots.flatMap((slot) =>
        slot.templates
          .filter((t) => activeFilter === 'all' || t.is_active)
          .map((t) => ({ ...t, slot })),
      ),
    [slots, activeFilter],
  )

  const selectedSlotKey = Form.useWatch('slotKey', form)
  const selectedSlot = (listQuery.data ?? []).find(
    (s) => slotKey(s) === selectedSlotKey,
  )

  const afterWrite = async (msg: string) => {
    message.success(msg)
    await qc.invalidateQueries({ queryKey: ['reply_templates'] })
  }

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const slot = (listQuery.data ?? []).find(
        (s) => slotKey(s) === values.slotKey,
      )
      if (!slot) throw new Error('请选择使用位置')
      return upsertReplyTemplate({
        id: editing && editing !== 'new' ? editing.id : undefined,
        context: slot.context,
        action: slot.action,
        facet: values.facet === GENERIC_FACET ? null : values.facet,
        title: values.title,
        body: values.body,
        sortOrder: values.sort_order ?? 0,
      })
    },
    onSuccess: async () => {
      setEditing(null)
      await afterWrite('已保存')
    },
    onError: (e) => message.error(formatReplyTemplateError(e)),
  })

  const activeMutation = useMutation({
    mutationFn: (input: { id: string; active: boolean }) =>
      setReplyTemplateActive(input.id, input.active),
    onSuccess: (_r, vars) => afterWrite(vars.active ? '已启用' : '已停用'),
    onError: (e) => message.error(formatReplyTemplateError(e)),
  })

  function openCreate() {
    setEditing('new')
    form.resetFields()
    form.setFieldsValue({
      slotKey: slots[0] ? slotKey(slots[0]) : undefined,
      facet: GENERIC_FACET,
      sort_order: 0,
    })
  }

  function openEdit(row: TemplateRow) {
    setEditing(row)
    form.setFieldsValue({
      slotKey: slotKey(row),
      facet: row.facet ?? GENERIC_FACET,
      title: row.title,
      body: row.body,
      sort_order: row.sort_order,
    })
  }

  const toggle = (row: TemplateRow) =>
    activeMutation.mutate({ id: row.id, active: !row.is_active })

  const columns: ColumnsType<TemplateRow> = [
    {
      title: '动作',
      width: 110,
      render: (_v, row) => slotActionLabel(row.slot),
    },
    {
      title: '细分',
      width: 130,
      render: (_v, row) =>
        row.facet ? (
          <Tag color="blue">{facetLabel(row.slot.facet_kind, row.facet)}</Tag>
        ) : (
          <Tag>通用</Tag>
        ),
    },
    { title: '标题', dataIndex: 'title', width: 140 },
    {
      title: '正文',
      dataIndex: 'body',
      render: (body: string) => (
        <Typography.Paragraph
          style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
          ellipsis={{ rows: 2, tooltip: body }}
        >
          {body}
        </Typography.Paragraph>
      ),
    },
    { title: '排序', dataIndex: 'sort_order', width: 70 },
    {
      title: '状态',
      dataIndex: 'is_active',
      width: 80,
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'default'}>
          {active ? '启用' : '停用'}
        </Tag>
      ),
    },
    {
      title: '更新于',
      dataIndex: 'updated_at',
      width: 150,
      render: (v: string) => formatCST(v),
    },
    {
      title: '操作',
      width: 120,
      fixed: 'right',
      render: (_v, row) => (
        <Space size={4}>
          <Tooltip title="编辑">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row)}
            />
          </Tooltip>
          <Button
            size="small"
            danger={row.is_active}
            loading={
              activeMutation.isPending &&
              activeMutation.variables?.id === row.id
            }
            onClick={() => toggle(row)}
          >
            {row.is_active ? '停用' : '启用'}
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space
          align="center"
          style={{ width: '100%', justifyContent: 'space-between' }}
          wrap
        >
          <Space orientation="vertical" size={4}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              回复模板
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              {isMobile
                ? '处理反馈和审核时快选的理由文本，点选后仍可修改。'
                : '处理反馈和审核时可快选的理由文本。点选后填入输入框仍可修改；修改或停用模板不影响已发出的回复。'}
            </Typography.Paragraph>
          </Space>
          <Space wrap>
            <Segmented
              value={activeFilter}
              options={[
                { value: 'active', label: '启用' },
                { value: 'all', label: '全部' },
              ]}
              onChange={(v) => setActiveFilter(v as ActiveFilter)}
            />
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                loading={listQuery.isFetching}
                onClick={() => void listQuery.refetch()}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={slots.length === 0}
              onClick={openCreate}
            >
              {isMobile ? '新建' : '新建模板'}
            </Button>
          </Space>
        </Space>
        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          <Segmented
            value={context}
            options={REPLY_CONTEXTS.map((c) => ({
              value: c,
              label: REPLY_CONTEXT_LABELS[c],
            }))}
            onChange={(v) => setContext(v as ReplyContext)}
          />
        </div>
      </Card>

      {listQuery.isError ? (
        <Alert
          type="error"
          showIcon
          title="回复模板加载失败"
          description={formatReplyTemplateError(listQuery.error)}
          action={
            <Button onClick={() => void listQuery.refetch()}>重试</Button>
          }
        />
      ) : null}

      <Card>
        <div className="admin-table-wrap">
          <div className="admin-desktop-table">
            <Table<TemplateRow>
              rowKey="id"
              columns={columns}
              dataSource={rows}
              loading={listQuery.isLoading}
              scroll={{ x: 1000 }}
              pagination={{ pageSize: 50, hideOnSinglePage: true }}
            />
          </div>
        </div>
        {/* 移动端卡片必须放在 admin-table-wrap 外，否则 < 992px 会被一起隐藏 */}
        <AdminMobileList<TemplateRow>
          rows={rows}
          rowKey={(row) => row.id}
          loading={listQuery.isLoading}
          renderItem={(row) => (
            <article
              className="admin-mobile-record"
              role="button"
              tabIndex={0}
              aria-label={`编辑模板：${row.title}`}
              onClick={() => openEdit(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  openEdit(row)
                }
              }}
            >
              <div className="admin-mobile-record__head">
                <div className="admin-mobile-record__title">{row.title}</div>
                <Tag color={row.is_active ? 'green' : 'default'}>
                  {row.is_active ? '启用' : '停用'}
                </Tag>
              </div>
              <div className="admin-mobile-record__badges">
                <Tag>{slotActionLabel(row.slot)}</Tag>
                <Tag color={row.facet ? 'blue' : undefined}>
                  {facetLabel(row.slot.facet_kind, row.facet)}
                </Tag>
              </div>
              <div className="admin-mobile-record__body">
                <Typography.Paragraph
                  ellipsis={{ rows: 3 }}
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {row.body}
                </Typography.Paragraph>
              </div>
              <AdminRecordActions
                leading={
                  <span>
                    排序 {row.sort_order} ·{' '}
                    {formatCST(row.updated_at, 'MM-DD HH:mm')}
                  </span>
                }
                actions={[
                  {
                    key: 'edit',
                    label: '编辑',
                    icon: <EditOutlined />,
                    onClick: () => openEdit(row),
                  },
                  {
                    key: 'toggle',
                    label: row.is_active ? '停用' : '启用',
                    icon: row.is_active ? (
                      <PauseCircleOutlined />
                    ) : (
                      <PlayCircleOutlined />
                    ),
                    danger: row.is_active,
                    loading:
                      activeMutation.isPending &&
                      activeMutation.variables?.id === row.id,
                    onClick: () => toggle(row),
                  },
                ]}
              />
            </article>
          )}
        />
      </Card>

      <Drawer
        title={editing === 'new' ? '新建回复模板' : '编辑回复模板'}
        open={editing !== null}
        width={isMobile ? '100%' : 520}
        onClose={() => setEditing(null)}
        destroyOnHidden
        extra={
          <Button
            type="primary"
            loading={saveMutation.isPending}
            onClick={() => form.submit()}
          >
            保存
          </Button>
        }
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => saveMutation.mutate(values)}
        >
          <Form.Item
            name="slotKey"
            label="使用位置"
            rules={[{ required: true }]}
          >
            <Select
              options={REPLY_CONTEXTS.map((c) => ({
                label: REPLY_CONTEXT_LABELS[c],
                options: (listQuery.data ?? [])
                  .filter((s) => s.context === c)
                  .map((s) => ({ value: slotKey(s), label: s.label })),
              }))}
              onChange={() => form.setFieldValue('facet', GENERIC_FACET)}
            />
          </Form.Item>
          <Form.Item
            name="facet"
            label="细分"
            extra="通用模板对该位置的所有记录显示；指定细分后只对匹配的记录显示，并排在通用模板前。"
          >
            <Select
              disabled={!selectedSlot?.facet_values}
              options={[
                { value: GENERIC_FACET, label: '通用' },
                ...(selectedSlot?.facet_values ?? []).map((v) => ({
                  value: v,
                  label: facetLabel(selectedSlot?.facet_kind ?? null, v),
                })),
              ]}
            />
          </Form.Item>
          <Form.Item
            name="title"
            label="标题（快选按钮文字）"
            rules={[
              { required: true, whitespace: true, message: '请输入标题' },
              { max: 20, message: '最多 20 字' },
            ]}
          >
            <Input maxLength={20} showCount />
          </Form.Item>
          <Form.Item
            name="body"
            label="正文（发给用户的理由）"
            rules={[
              { required: true, whitespace: true, message: '请输入正文' },
            ]}
          >
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 8 }}
              maxLength={selectedSlot?.max_length}
              showCount
            />
          </Form.Item>
          <Form.Item name="sort_order" label="排序（小的在前）">
            <InputNumber min={-10000} max={10000} precision={0} />
          </Form.Item>
          {editing && editing !== 'new' ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              创建于 {formatCST(editing.created_at)} · 更新于{' '}
              {formatCST(editing.updated_at)}
            </Typography.Text>
          ) : null}
        </Form>
      </Drawer>
    </Space>
  )
}
